import { MaterialIcon } from "@website/icons/material";
import { useTable } from "@website/ui/datepicker/components/table/table.hook";
import styles from "@website/ui/datepicker/components/table/table.module.scss";
import { TablePropertiesType } from "@website/ui/datepicker/components/table/table.types";
import { Tooltip } from "@website/ui/tooltip/tooltip";
import { numberConvert } from "@website/utils/number-convert";
import { FC, memo } from "react";

const WEEK_ABBR: Record<string, Array<string>> = {
  persian: ["ش", "ی", "د", "س", "چ", "پ", "ج"],
  gregorian: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
};
const MONTH_NAMES: Record<string, Array<string>> = {
  persian: [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند"
  ],
  gregorian: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ]
};

const Table: FC<TablePropertiesType> = (properties) => {
  const { calendarLocale, year, month, type, view, isLabelTop } = properties;
  const { days } = useTable(properties);
  const isFlat = type === "flat";

  return (
    <div
      className={`${styles["container"]} ${
        isFlat ? styles["container-flat"] : ""
      } ${styles["rtl" || "ltr"]}`}
    >
      <div
        className={`${
          isFlat && view !== "mobile"
            ? styles["week-names-container-desktop-flat"]
            : !isFlat && view !== "mobile"
              ? `${styles["week-names-container-desktop"]} ${styles["week-names-container-desktop__default"]}`
              : styles["week-names-container-desktop"]
        } `}
      >
        {[1, 2, 3, 4, 5, 6, 7].map((week) => (
          <div className={styles["week-name"]} key={week}>
            <div className={isFlat ? styles["aspect-ratio-content"] : ""}>
              {WEEK_ABBR[calendarLocale]?.[week - 1] ||
                WEEK_ABBR.gregorian[week - 1]}
            </div>
          </div>
        ))}
      </div>
      <div
        className={`${styles["month-name"]} ${
          view === "mobile" ? "" : styles["month-name__default"]
        } ${isLabelTop ? "absolute inset-x-0 top-0 z-[-1] !mt-4" : ""}`}
      >
        {(MONTH_NAMES[calendarLocale] || MONTH_NAMES.gregorian)[month - 1]}{" "}
        {numberConvert(year, {
          numberOnly: true,
          locale: "fa"
        })}
      </div>
      {[0, 1, 2, 3, 4, 5].map((row, rowIndex) => {
        if (
          days[rowIndex * 7]?.value !== -1 || // first cell of row
          days[rowIndex * 7 + 6]?.value !== -1 // last cell of row
        ) {
          return (
            <div
              className={`${isFlat ? styles["row-flat"] : styles["row"]} ${
                !isFlat && view === "mobile"
                  ? ""
                  : !isFlat && view !== "mobile"
                    ? styles["row__default"]
                    : ""
              }`}
              key={row}
            >
              {[0, 1, 2, 3, 4, 5, 6].map((cell, cellIndex) => {
                const index = rowIndex * 7 + cellIndex;
                const cellText =
                  days[index]?.value === -1 ? "" : days[index]?.value;
                const cellContent = days[index]?.content;
                const state = days[index]?.state;
                const isToday = days[index]?.isToday;
                const nonInstantAvailability =
                  days[index]?.nonInstantAvailability;
                const availability = days[index]?.availability;
                const tooltip = days[index]?.tooltip;
                return (
                  <div
                    className={`${styles["cell"]} ${
                      isFlat
                        ? styles["cell-flat"]
                        : view === "mobile"
                          ? styles["cell-popover__mobile"]
                          : styles["cell-popover"]
                    } ${isToday ? styles["today"] : ""} ${
                      state ? styles[state] : ""
                    }`}
                    key={cell}
                    onClick={days[index]?.onClick}
                  >
                    <div
                      className={`${styles["cell-inside"]} ${
                        isFlat
                          ? styles["cell-inside-flat"]
                          : view === "mobile"
                            ? styles["cell-inside-popover__mobile"]
                            : styles["cell-inside-popover__default"]
                      }`}
                    >
                      <Tooltip
                        className="!h-max !w-full"
                        placement="top"
                        title={tooltip}
                        titleClassName={`!left-0 ${!tooltip && "hidden"}`}
                      >
                        {nonInstantAvailability === 0 && availability > 0 && (
                          <MaterialIcon
                            className="text-warning flex !w-full self-end"
                            name="bolt"
                          />
                        )}
                        <div className="w-full text-center">
                          {numberConvert(cellText, {
                            numberOnly: true,
                            locale: "fa"
                          })}
                        </div>

                        {cellContent ? (
                          <span className={styles["cell-inside-data"]}>
                            {cellContent}
                          </span>
                        ) : null}
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const TableMemoized = memo(Table);
export { TableMemoized as Table };
